import { SearchDataProps } from '@/pages/Dashboard';
import axios, { AxiosResponse } from 'axios';
import { apiClient, authApiClient } from './api-client';

const TENANT_API_BASE_URL = import.meta.env.VITE_TENANT_API_URL || 'https://app-lawma-prod-ai-api-westus2-001-g9cshwd4bvhyh3bj.westus2-01.azurewebsites.net';

import type {
  LoginRequest, LoginResponse, AeoLaunchResponse, TenantAdmin, ChangePasswordRequest, TenantAdminProfileResponse,
  ForgotPasswordRequest, ResetPasswordRequest, MessageResponse, ResetTokenValidateResponse,
  TenantBrandingResponse,
  DashboardAnalytics, DashboardEventAnalytics,
  AppointmentFilterParams, AppointmentListResponse, AppointmentStatsResponse, Appointment,
  AppointmentStatusUpdateRequest, AppointmentConfig, AppointmentToggleRequest,
  AppointmentConfigUpdateRequest, WidgetConfigResponse, WidgetConfig, WidgetConfigUpdateResponse,
  Asset, Document, DocumentRequest, Product, ProductRequest, Service, ServiceRequest,
  TenantSettingsResponse, TenantSettingsUpdateRequest, ConversationsResponse, Conversation,
  ConversationStats, NoSayListResponse, EmbeddingSearchGuardrailsResponse, EmbeddingSearchGuardrailsUpdate,
  RedactionSettingsResponse, RedactionSettingsRequest,
  ApiKeyResponse, ApiKeyCreateRequest, ApiKeyCreateResponse,
  PreviewApiKeyResponse, PreviewApiKeyCreateRequest, PreviewApiKeyCreateResponse,
  Integration, IntegrationRequest, WordPressImportRequest, ImportResult,
  EmbeddingGenerationRequest, EmbeddingGenerationResponse, SimilaritySearchRequest,
  SimilaritySearchResponse, EmbeddingListResponse, EmbeddingStats, GoogleReview, ManualReviewRequest,
  PrimerQuestion, PrimerQuestionRequest, PrimerQuestionForWidget, PrimerQuestionConfig,
  QuestionVersionsResponse, QuestionScoreHistoryResponse,
  PrimerQuestionConfigUpdate, ContactInfo, ContactInfoRequest,
  CTA, CTARequest, CTAWidgetFormat, CTAConfigResponse, CTAConfigUpdateRequest,
  CTAMetricsParams, CTAMetrics, MessageTextMetricsParams, MessageTextMetrics,
  WidgetHomepageConfig, WidgetHomepageConfigRequest, WidgetHomepageContentResponse,
  MultipleHomepageCreateRequest, MultipleHomepageUpdateRequest,
  FetchReviewsResponse, LLMSGenerateResponse, LLMSGenerationStatusResponse, LLMSCacheStatus,
  LLMSClearCacheResponse, TenantCRMLead, TenantCRMLeadUpdate, PaginatedResponse,
  QuestionLabel, ConversionEventsResponse,
  FullSDLResponse, SDLMetadataOption, SDLComparisonResponse,
  FormType, SubmissionStatus, PaginatedFormSubmissions, FormSubmissionStats,
  AIAssistantQueryParams, AIAssistantQueryResponse,
  QuestionOpsPlanParams, QuestionOpsPlanResponse,
  AIAuthorityReportList, AIAuthorityDataFull,
} from './api-types';

// Re-export all types so existing imports like `import { tenantApi, SomeType } from '../lib/tenant-api'` still work
export * from './api-types';

export const tenantApi = {

  // Authentication
  getTenantBranding: async (subdomain: string): Promise<TenantBrandingResponse> => {
    const response: AxiosResponse<TenantBrandingResponse> = await authApiClient.get(
      `/tenant/auth/branding/${encodeURIComponent(subdomain)}`,
    );
    return response.data;
  },

  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response: AxiosResponse<LoginResponse> = await authApiClient.post('/tenant/auth/login', data);

    // Store token and tenant ID
    localStorage.setItem('tenant_auth_token', response.data.access_token);
    localStorage.setItem('tenant_id', response.data.tenant.id);

    return response.data;
  },

  getCurrentAdmin: async (): Promise<TenantAdmin> => {
    const response: AxiosResponse<TenantAdmin> = await authApiClient.get('/tenant/auth/me');
    return response.data;
  },

  changePassword: async (data: ChangePasswordRequest): Promise<TenantAdminProfileResponse> => {
    const response: AxiosResponse<TenantAdminProfileResponse> = await authApiClient.put(
      '/tenant/auth/profile',
      data,
    );
    return response.data;
  },

  requestPasswordReset: async (data: ForgotPasswordRequest): Promise<MessageResponse> => {
    const response: AxiosResponse<MessageResponse> = await authApiClient.post(
      '/tenant/auth/forgot-password',
      data,
    );
    return response.data;
  },

  validateResetToken: async (token: string): Promise<ResetTokenValidateResponse> => {
    const response: AxiosResponse<ResetTokenValidateResponse> = await authApiClient.get(
      '/tenant/auth/reset-password/validate',
      { params: { token } },
    );
    return response.data;
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<MessageResponse> => {
    const response: AxiosResponse<MessageResponse> = await authApiClient.post(
      '/tenant/auth/reset-password',
      data,
    );
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('tenant_auth_token');
    localStorage.removeItem('tenant_id');
  },

  // AEO PLATFORM one-click handoff — mints the signed token + redirect URL that
  // launches the current tenant admin into AEO PLATFORM (server-side mint; the
  // token never originates in this SPA). Auth'd as the logged-in admin.
  launchAeoPlatform: async (): Promise<AeoLaunchResponse> => {
    const response: AxiosResponse<AeoLaunchResponse> = await authApiClient.post('/tenant/aeo/launch');
    return response.data;
  },

  // Dashboard
  getDashboardAnalytics: async (): Promise<DashboardAnalytics> => {
    const response: AxiosResponse<DashboardAnalytics> = await apiClient.get('/tenant/dashboard/analytics');
    return response.data;
  },

  // Dashboard Event Analytics
  getDashboardEventAnalytics: async (searchData: SearchDataProps): Promise<DashboardEventAnalytics> => {
    let url = '/tenant/dashboard/event-analytics';
    if (searchData.startDate && searchData.endDate) {
      url += (url.includes('?') ? '&' : '?') + `startDate=${searchData.startDate}&endDate=${searchData.endDate}`;
    }
    if (searchData.conversationTrafficType) {
      url += (url.includes('?') ? '&' : '?') + `conversationTrafficType=${searchData.conversationTrafficType}`;
    }
    if (searchData.generationType) {
      url += (url.includes('?') ? '&' : '?') + `generationType=${searchData.generationType}`;
    }
    const response: AxiosResponse<DashboardEventAnalytics> = await apiClient.get(url);
    return response.data;
  },

  // Appointments
  getAppointments: async (params?: AppointmentFilterParams): Promise<AppointmentListResponse> => {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.query) queryParams.append('query', params.query);

    const url = `/appointments/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response: AxiosResponse<AppointmentListResponse> = await apiClient.get(url);
    return response.data;
  },

  getAppointmentStats: async (): Promise<AppointmentStatsResponse> => {
    const response: AxiosResponse<AppointmentStatsResponse> = await apiClient.get('/appointments/stats');
    return response.data;
  },

  getAppointment: async (appointmentId: string): Promise<Appointment> => {
    const response: AxiosResponse<Appointment> = await apiClient.get(`/appointments/${appointmentId}`);
    return response.data;
  },

  updateAppointmentStatus: async (appointmentId: string, data: AppointmentStatusUpdateRequest): Promise<Appointment> => {
    const response: AxiosResponse<Appointment> = await apiClient.patch(`/appointments/${appointmentId}/status`, data);
    return response.data;
  },

  deleteAppointment: async (appointmentId: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.delete(`/appointments/${appointmentId}`);
    return response.data;
  },


  // Appointment Configuration
  getAppointmentConfig: async (): Promise<AppointmentConfig | null> => {
    try {
      const response: AxiosResponse<AppointmentConfig> = await apiClient.get('/appointment-config/');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // No configuration exists yet
      }
      throw error;
    }
  },

  getAppointmentConfigs: async (): Promise<AppointmentConfig[]> => {
    const response: AxiosResponse<AppointmentConfig[]> = await apiClient.get('/appointment-configs');
    return response.data;
  },

  toggleAppointmentConfig: async (data: AppointmentToggleRequest): Promise<AppointmentConfig> => {
    const response: AxiosResponse<AppointmentConfig> = await apiClient.post('/appointment-config/toggle', data);
    return response.data;
  },

  updateAppointmentConfig: async (data: AppointmentConfigUpdateRequest): Promise<AppointmentConfig> => {
    const response: AxiosResponse<AppointmentConfig> = await apiClient.put('/appointment-config/', data);
    return response.data;
  },

  // Widget Configuration
  getWidgetConfig: async (): Promise<WidgetConfigResponse> => {
    const response: AxiosResponse<WidgetConfigResponse> = await apiClient.get('/widget-config');
    return response.data;
  },

  updateWidgetConfig: async (data: Partial<WidgetConfig>): Promise<WidgetConfigUpdateResponse> => {
    const response: AxiosResponse<WidgetConfigUpdateResponse> = await apiClient.post('/widget-config', data);
    return response.data;
  },

  // Assets
  getAssets: async (skip: number = 0, limit: number = 100): Promise<Asset[]> => {
    const response: AxiosResponse<Asset[]> = await apiClient.get(`/tenant/assets?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  uploadAsset: async (file: File, description?: string, tags?: string[], file_name?: string): Promise<Asset> => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }
    if (tags && tags.length > 0) {
      formData.append('tags', tags.join(','));
    }
    if (file_name) {
      formData.append('file_name', file_name);
    }

    const response: AxiosResponse<Asset> = await apiClient.post('/tenant/assets/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getAssetsWithTags: async (tags?: string[], assetType?: string, skip: number = 0, limit: number = 100): Promise<Asset[]> => {
    const params = new URLSearchParams();
    if (tags && tags.length > 0) {
      params.append('tags', tags.join(','));
    }
    if (assetType) {
      params.append('asset_type', assetType);
    }
    params.append('skip', skip.toString());
    params.append('limit', limit.toString());

    const response: AxiosResponse<Asset[]> = await apiClient.get(`/tenant/assets?${params.toString()}`);
    return response.data;
  },

  updateAssetTags: async (id: string, tags: string[]): Promise<Asset> => {
    const formData = new FormData();
    formData.append('tags', tags.join(','));

    const response: AxiosResponse<Asset> = await apiClient.put(`/tenant/assets/${id}/tags`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getAssetTags: async (): Promise<{ tags: string[]; count: number }> => {
    const response: AxiosResponse<{ tags: string[]; count: number }> = await apiClient.get('/tenant/assets/tags');
    return response.data;
  },

  deleteAsset: async (id: string): Promise<void> => {
    await apiClient.delete(`/tenant/assets/${id}`);
  },

  // Documents (Knowledge Base)
  getDocuments: async (category?: string, document_type?: string): Promise<Document[]> => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (document_type) params.append('document_type', document_type);

    const response: AxiosResponse<Document[]> = await apiClient.get(`/tenant/documents?${params.toString()}`);
    return response.data;
  },

  createDocument: async (data: DocumentRequest): Promise<Document> => {
    const response: AxiosResponse<Document> = await apiClient.post('/tenant/documents', data);
    return response.data;
  },

  updateDocument: async (id: string, data: DocumentRequest): Promise<Document> => {
    const response: AxiosResponse<Document> = await apiClient.put(`/tenant/documents/${id}`, data);
    return response.data;
  },

  deleteDocument: async (id: string): Promise<void> => {
    await apiClient.delete(`/tenant/documents/${id}`);
  },

  uploadDocuments: async (file: File, generateEmbeddings: boolean = true): Promise<{ message: string; filename: string; results: any }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('generate_embeddings', generateEmbeddings.toString());

    const response: AxiosResponse<{ message: string; filename: string; results: any }> = await apiClient.post('/tenant/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },


  uploadProducts: async (file: File, generateEmbeddings: boolean = true): Promise<{ message: string; filename: string; results: any }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('generate_embeddings', generateEmbeddings.toString());

    const response: AxiosResponse<{ message: string; filename: string; results: any }> = await apiClient.post('/tenant/products/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadServices: async (file: File, generateEmbeddings: boolean = true): Promise<{ message: string; filename: string; results: any }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('generate_embeddings', generateEmbeddings.toString());

    const response: AxiosResponse<{ message: string; filename: string; results: any }> = await apiClient.post('/tenant/services/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Products
  getProducts: async (category?: string, status?: string): Promise<Product[]> => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (status) params.append('status', status);

    const response: AxiosResponse<Product[]> = await apiClient.get(`/tenant/products?${params.toString()}`);
    return response.data;
  },

  createProduct: async (data: ProductRequest): Promise<Product> => {
    const response: AxiosResponse<Product> = await apiClient.post('/tenant/products', data);
    return response.data;
  },

  updateProduct: async (id: string, data: ProductRequest): Promise<Product> => {
    const response: AxiosResponse<Product> = await apiClient.put(`/tenant/products/${id}`, data);
    return response.data;
  },

  deleteProduct: async (id: string): Promise<void> => {
    await apiClient.delete(`/tenant/products/${id}`);
  },

  // Services
  getServices: async (category?: string, status?: string): Promise<Service[]> => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (status) params.append('status', status);

    const response: AxiosResponse<Service[]> = await apiClient.get(`/tenant/services?${params.toString()}`);
    return response.data;
  },

  createService: async (data: ServiceRequest): Promise<Service> => {
    const response: AxiosResponse<Service> = await apiClient.post('/tenant/services', data);
    return response.data;
  },

  updateService: async (id: string, data: ServiceRequest): Promise<Service> => {
    const response: AxiosResponse<Service> = await apiClient.put(`/tenant/services/${id}`, data);
    return response.data;
  },

  deleteService: async (id: string): Promise<void> => {
    await apiClient.delete(`/tenant/services/${id}`);
  },

  // Tenant Settings
  getTenantSettings: async (): Promise<TenantSettingsResponse> => {
    const response: AxiosResponse<TenantSettingsResponse> = await authApiClient.get('/tenant/settings');
    return response.data;
  },

  updateTenantSettings: async (data: TenantSettingsUpdateRequest): Promise<TenantSettingsResponse> => {
    const response: AxiosResponse<TenantSettingsResponse> = await authApiClient.put('/tenant/settings', data);
    return response.data;
  },

  // Conversations
  getConversations: async (limit: number = 50, offset: number = 0, user_identifier?: string, search?: string, date_from?: string, date_to?: string, status?: string): Promise<ConversationsResponse> => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    if (search) params.append('search', search);
    if (user_identifier) params.append('user_identifier', user_identifier);
    if (date_from) params.append('date_from', date_from);
    if (date_to) params.append('date_to', date_to);
    if (status) params.append('status', status);

    const response: AxiosResponse<ConversationsResponse> = await apiClient.get(`/conversations?${params.toString()}`);
    return response.data;
  },

  getConversation: async (id: string): Promise<Conversation> => {
    const response: AxiosResponse<Conversation> = await apiClient.get(`/conversations/${id}`);
    return response.data;
  },

  deleteConversation: async (id: string): Promise<void> => {
    await apiClient.delete(`/conversations/${id}`);
  },

  getConversationStats: async (date_from?: string, date_to?: string): Promise<ConversationStats> => {
    const params = new URLSearchParams();
    if (date_from) params.append('date_from', date_from);
    if (date_to) params.append('date_to', date_to);

    const url = `/conversations/stats${params.toString() ? '?' + params.toString() : ''}`;
    const response: AxiosResponse<ConversationStats> = await apiClient.get(url);
    return response.data;
  },

  getNoSayList: async (): Promise<NoSayListResponse[]> => {
    const response: AxiosResponse<NoSayListResponse[]> = await apiClient.get('/guardrails/no-say-list');
    return response.data;
  },


  deleteNoSay: async (noSayId: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.delete(`/guardrails/no-say-list/${noSayId}`);
    return response.data;
  },

  createNoSayList: async (noSay: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.post('/guardrails/no-say-list', { no_say: noSay });
    return response.data;
  },

  updateNoSayList: async (noSay: string, noSayId: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.put(`/guardrails/no-say-list/${noSayId}`, { no_say: noSay });
    return response.data;
  },

  getEmbeddingSearchGuardrails: async (): Promise<EmbeddingSearchGuardrailsResponse> => {
    const response: AxiosResponse<EmbeddingSearchGuardrailsResponse> = await apiClient.get('/guardrails/embedding-search');
    return response.data;
  },

  updateEmbeddingSearchGuardrails: async (
    data: EmbeddingSearchGuardrailsUpdate
  ): Promise<EmbeddingSearchGuardrailsResponse> => {
    const response: AxiosResponse<EmbeddingSearchGuardrailsResponse> = await apiClient.put('/guardrails/embedding-search', data);
    return response.data;
  },

  getRedactionSettings: async (): Promise<RedactionSettingsResponse> => {
    const response: AxiosResponse<RedactionSettingsResponse> = await apiClient.get('/guardrails/redaction-settings');
    return response.data;
  },

  updateRedactionSettings: async (data: RedactionSettingsRequest): Promise<RedactionSettingsResponse> => {
    const response: AxiosResponse<RedactionSettingsResponse> = await apiClient.put('/guardrails/redaction-settings', data);
    return response.data;
  },

  // API Key management
  getApiKeys: async (): Promise<ApiKeyResponse[]> => {
    const response: AxiosResponse<ApiKeyResponse[]> = await authApiClient.get('/tenant/api-keys');
    return response.data;
  },

  createApiKey: async (data: ApiKeyCreateRequest): Promise<ApiKeyCreateResponse> => {
    const tenantId = localStorage.getItem('tenant_id');
    if (!tenantId) {
      throw new Error('Tenant ID not found. Please log in again.');
    }

    const requestData = {
      ...data,
      tenant_id: tenantId
    };

    const response: AxiosResponse<ApiKeyCreateResponse> = await authApiClient.post('/tenant/api-keys', requestData);
    return response.data;
  },

  toggleApiKeyStatus: async (apiKeyId: string): Promise<{ message: string; is_active: boolean }> => {
    const response: AxiosResponse<{ message: string; is_active: boolean }> = await authApiClient.patch(`/tenant/api-keys/${apiKeyId}/toggle`);
    return response.data;
  },

  deleteApiKey: async (apiKeyId: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await authApiClient.delete(`/tenant/api-keys/${apiKeyId}`);
    return response.data;
  },

  // Preview API Key Management
  getPreviewApiKey: async (): Promise<PreviewApiKeyResponse> => {
    const response: AxiosResponse<PreviewApiKeyResponse> = await authApiClient.get('/tenant/preview-api-key');
    return response.data;
  },

  createPreviewApiKey: async (data: PreviewApiKeyCreateRequest): Promise<PreviewApiKeyCreateResponse> => {
    const response: AxiosResponse<PreviewApiKeyCreateResponse> = await authApiClient.post('/tenant/preview-api-key', data);
    return response.data;
  },

  deletePreviewApiKey: async (): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await authApiClient.delete('/tenant/preview-api-key');
    return response.data;
  },


  // Integrations
  getIntegrations: async (): Promise<Integration[]> => {
    const response: AxiosResponse<Integration[]> = await apiClient.get('/integrations');
    return response.data;
  },

  createIntegration: async (data: IntegrationRequest): Promise<Integration> => {
    const response: AxiosResponse<Integration> = await apiClient.post('/integrations', data);
    return response.data;
  },

  updateIntegration: async (id: string, data: IntegrationRequest): Promise<Integration> => {
    const response: AxiosResponse<Integration> = await apiClient.put(`/integrations/${id}`, data);
    return response.data;
  },

  deleteIntegration: async (id: string): Promise<void> => {
    await apiClient.delete(`/integrations/${id}`);
  },

  testIntegration: async (id: string): Promise<{ success: boolean; message?: string; error?: string; site_info?: any }> => {
    const response: AxiosResponse<{ success: boolean; message?: string; error?: string; site_info?: any }> = await apiClient.post(`/integrations/${id}/test`);
    return response.data;
  },

  getWordPressProducts: async (integrationId: string): Promise<Record<string, any>> => {
    const response: AxiosResponse<Record<string, any>> = await apiClient.get(`/integrations/${integrationId}/products`);
    return response.data;
  },

  getWordPressPosts: async (integrationId: string): Promise<Record<string, any>> => {
    const response: AxiosResponse<Record<string, any>> = await apiClient.get(`/integrations/${integrationId}/docs`);
    return response.data;
  },

  getWordPressServices: async (integrationId: string): Promise<Record<string, any>> => {
    const response: AxiosResponse<Record<string, any>> = await apiClient.get(`/integrations/${integrationId}/services`);
    return response.data;
  },

  importWordPressContent: async (data: WordPressImportRequest): Promise<ImportResult> => {
    const response: AxiosResponse<ImportResult> = await apiClient.post(`/integrations/import`, data);
    return response.data;
  },

  // Embeddings
  generateEmbeddings: async (data: EmbeddingGenerationRequest): Promise<EmbeddingGenerationResponse> => {
    const response: AxiosResponse<EmbeddingGenerationResponse> = await apiClient.post('/embeddings/create', data);
    return response.data;
  },

  regenerateSelectedEmbeddings: async (data: { content_types?: string[]; content_ids?: string[]; replace_existing?: boolean }): Promise<{ message: string; tenant_id: string; content_types?: string[]; content_ids_count: number }> => {
    const response: AxiosResponse<{ message: string; tenant_id: string; content_types?: string[]; content_ids_count: number }> = await apiClient.post('/embeddings/regenerate-selected', data);
    return response.data;
  },

  searchSimilarContent: async (data: SimilaritySearchRequest): Promise<SimilaritySearchResponse> => {
    const response: AxiosResponse<SimilaritySearchResponse> = await apiClient.post('/embeddings/search', data);
    return response.data;
  },

  listEmbeddings: async (contentType?: string, limit: number = 50, offset: number = 0, query?: string): Promise<EmbeddingListResponse> => {
    const params = new URLSearchParams();
    if (contentType) params.append('content_type', contentType);
    if (query) params.append('query', query);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    const response: AxiosResponse<EmbeddingListResponse> = await apiClient.get(`/embeddings/list?${params.toString()}`);
    return response.data;
  },

  listReviews: async (): Promise<GoogleReview[]> => {
    const response: AxiosResponse<GoogleReview[]> = await apiClient.get(`/tenant/reviews`);
    return response.data;
  },

  createManualReview: async (data: ManualReviewRequest): Promise<GoogleReview> => {
    const response: AxiosResponse<GoogleReview> = await apiClient.post('/tenant/reviews', data);
    return response.data;
  },

  updateManualReview: async (id: string, data: ManualReviewRequest): Promise<GoogleReview> => {
    const response: AxiosResponse<GoogleReview> = await apiClient.put(`/tenant/reviews/${id}`, data);
    return response.data;
  },

  deleteManualReview: async (id: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.delete(`/tenant/reviews/${id}`);
    return response.data;
  },

  deleteEmbeddingById: async (embeddingId: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.delete(`/embeddings/${embeddingId}`);
    return response.data;
  },

  deleteEmbeddings: async (contentType: string, contentId: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.delete(`/embeddings/content/${contentType}/${contentId}`);
    return response.data;
  },

  regenerateAllEmbeddings: async (): Promise<{ message: string; tenant_id: string }> => {
    const response: AxiosResponse<{ message: string; tenant_id: string }> = await apiClient.post('/embeddings/regenerate-all');
    return response.data;
  },

  getEmbeddingStats: async (): Promise<EmbeddingStats> => {
    const response: AxiosResponse<EmbeddingStats> = await apiClient.get('/embeddings/stats');
    return response.data;
  },

  // Prompts
  getPrompts: async (): Promise<any[]> => {
    const response: AxiosResponse<any[]> = await apiClient.get('/prompts/');
    return response.data;
  },

  getPromptTypes: async (): Promise<{ prompt_types: string[] }> => {
    const response: AxiosResponse<{ prompt_types: string[] }> = await apiClient.get('/prompts/types');
    return response.data;
  },

  updatePrompt: async (data: { custom_content?: string; is_enabled: boolean }): Promise<{ message: string }> => {
    // Always use general endpoint
    const response: AxiosResponse<{ message: string }> = await apiClient.put('/prompts/general', data);
    return response.data;
  },

  deletePromptCustomization: async (): Promise<{ message: string }> => {
    // Always use general endpoint
    const response: AxiosResponse<{ message: string }> = await apiClient.delete('/prompts/general');
    return response.data;
  },

  previewPrompt: async (data: { prompt_type: string; custom_content?: string; goal: string }): Promise<{ preview_content: string; variables_used: any }> => {
    // Simplify to general prompt preview
    const simplifiedData = {
      prompt_type: 'general',
      custom_content: data.custom_content,
      goal: data.goal
    };
    const response: AxiosResponse<{ preview_content: string; variables_used: any }> = await apiClient.post('/prompts/preview', simplifiedData);
    return response.data;
  },

  clearPromptCache: async (): Promise<{ message: string; tenant_id: string; cache_cleared: boolean }> => {
    const response: AxiosResponse<{ message: string; tenant_id: string; cache_cleared: boolean }> = await apiClient.post('/prompts/cache/clear');
    return response.data;
  },

  // Primer Questions
  getPrimerQuestions: async (): Promise<PrimerQuestion[]> => {
    const response: AxiosResponse<PrimerQuestion[]> = await apiClient.get('/questions');
    // Retired questions are internal measurement history (kept server-side for
    // LARS score continuity) — no tenant surface ever shows them.
    return response.data.filter(q => !q.retired_at);
  },

  // Full distributable SDL reconstructed server-side from persisted questions + metadata.
  getDistributableSdl: async (): Promise<Record<string, unknown>> => {
    const response: AxiosResponse<Record<string, unknown>> = await apiClient.get('/questions/sdl/full');
    return response.data;
  },

  createPrimerQuestion: async (data: PrimerQuestionRequest): Promise<PrimerQuestion> => {
    const response: AxiosResponse<PrimerQuestion> = await apiClient.post('/questions', data);
    return response.data;
  },

  updatePrimerQuestion: async (id: string, data: Partial<PrimerQuestionRequest>): Promise<PrimerQuestion> => {
    const response: AxiosResponse<PrimerQuestion> = await apiClient.put(`/questions/${id}`, data);
    return response.data;
  },

  // Baselined questions are retired server-side instead of deleted; the response flags it.
  deletePrimerQuestion: async (id: string): Promise<{ message?: string; retired?: boolean }> => {
    const response: AxiosResponse<{ message?: string; retired?: boolean }> = await apiClient.delete(`/questions/${id}`);
    return response.data;
  },

  getPrimerQuestionAnswer: async (id: string): Promise<{ id: string; question: string; answer: string }> => {
    const response: AxiosResponse<{ id: string; question: string; answer: string }> = await apiClient.get(`/questions/${id}/answer`);
    return response.data;
  },

  getQuestionVersions: async (id: string): Promise<QuestionVersionsResponse> => {
    const response: AxiosResponse<QuestionVersionsResponse> = await apiClient.get(`/questions/${id}/versions`);
    return response.data;
  },

  getQuestionScoreHistory: async (id: string): Promise<QuestionScoreHistoryResponse> => {
    const response: AxiosResponse<QuestionScoreHistoryResponse> = await apiClient.get(`/questions/${id}/score-history`);
    return response.data;
  },

  getPrimerQuestionChildren: async (id: string): Promise<PrimerQuestionForWidget[]> => {
    const response: AxiosResponse<PrimerQuestionForWidget[]> = await apiClient.get(`/questions/${id}/children`);
    return response.data;
  },

  getPrimerQuestionsForWidget: async (tenantId: string): Promise<PrimerQuestionForWidget[]> => {
    const response: AxiosResponse<PrimerQuestionForWidget[]> = await apiClient.get(`/primer-questions/widget/${tenantId}`);
    return response.data;
  },

  getFAQQuestionsForWidget: async (tenantId: string): Promise<any[]> => {
    const response: AxiosResponse<any[]> = await apiClient.get(`/faq-questions/widget/${tenantId}`);
    return response.data;
  },

  // Primer Question Relationship Management
  addParentToQuestion: async (questionId: string, parentId: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.post(`/questions/${questionId}/add-parent`, { parent_id: parentId });
    return response.data;
  },

  removeParentFromQuestion: async (questionId: string, parentId: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.delete(`/questions/${questionId}/remove-parent`, { data: { parent_id: parentId } });
    return response.data;
  },

  // Primer Question Configuration
  getPrimerQuestionConfig: async (): Promise<PrimerQuestionConfig> => {
    const response: AxiosResponse<PrimerQuestionConfig> = await apiClient.get('/questions/config');
    return response.data;
  },

  updatePrimerQuestionConfig: async (data: PrimerQuestionConfigUpdate): Promise<PrimerQuestionConfig> => {
    const response: AxiosResponse<PrimerQuestionConfig> = await apiClient.put('/questions/config', data);
    return response.data;
  },

  // Contact Information
  getContactInfo: async (): Promise<ContactInfo | null> => {
    const response: AxiosResponse<ContactInfo | null> = await apiClient.get('/contact');
    return response.data;
  },

  createOrUpdateContactInfo: async (data: ContactInfoRequest): Promise<ContactInfo> => {
    const response: AxiosResponse<ContactInfo> = await apiClient.post('/contact', data);
    return response.data;
  },

  // CTA Management
  getCTAs: async (): Promise<CTA[]> => {
    const response: AxiosResponse<CTA[]> = await apiClient.get('/ctas');
    return response.data;
  },

  createCTA: async (data: CTARequest): Promise<CTA> => {
    const response: AxiosResponse<CTA> = await apiClient.post('/ctas', data);
    return response.data;
  },

  updateCTA: async (id: string, data: Partial<CTARequest>): Promise<CTA> => {
    const response: AxiosResponse<CTA> = await apiClient.put(`/ctas/${id}`, data);
    return response.data;
  },

  deleteCTA: async (id: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.delete(`/ctas/${id}`);
    return response.data;
  },

  setDefaultCTA: async (id: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.post(`/ctas/${id}/set-default`);
    return response.data;
  },

  getCTAWidgetFormat: async (id: string): Promise<CTAWidgetFormat> => {
    const response: AxiosResponse<CTAWidgetFormat> = await apiClient.get(`/ctas/${id}/widget-format`);
    return response.data;
  },

  // Primer Question CTA Assignment
  getPrimerQuestionCTAs: async (questionId: string): Promise<CTA[]> => {
    const response: AxiosResponse<CTA[]> = await apiClient.get(`/questions/${questionId}/ctas`);
    return response.data;
  },

  getAllPrimerQuestionCTAs: async (): Promise<Record<string, CTA[]>> => {
    const response: AxiosResponse<Record<string, CTA[]>> = await apiClient.get('/questions/ctas/batch');
    return response.data;
  },

  assignCTAToPrimerQuestion: async (questionId: string, ctaId: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.post(`/questions/${questionId}/ctas`, { cta_id: ctaId });
    return response.data;
  },

  removeCTAFromPrimerQuestion: async (questionId: string, ctaId: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.delete(`/questions/${questionId}/ctas/${ctaId}`);
    return response.data;
  },

  // Asset Markdown Generation
  getAssetMarkdown: async (assetId: string): Promise<{ asset_id: string; file_name: string; asset_type: string; markdown: string; public_url: string }> => {
    const response: AxiosResponse<{ asset_id: string; file_name: string; asset_type: string; markdown: string; public_url: string }> = await apiClient.get(`/assets/${assetId}/markdown`);
    return response.data;
  },

  // Widget Homepage Configuration
  getWidgetHomepageConfig: async (): Promise<WidgetHomepageConfig | null> => {
    const response: AxiosResponse<WidgetHomepageConfig | null> = await apiClient.get('/tenant/widget-homepage/config');
    return response.data;
  },

  updateWidgetHomepageConfig: async (data: WidgetHomepageConfigRequest): Promise<WidgetHomepageConfig> => {
    const response: AxiosResponse<WidgetHomepageConfig> = await apiClient.put('/tenant/widget-homepage/config', data);
    return response.data;
  },

  deleteWidgetHomepageConfig: async (): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.delete('/tenant/widget-homepage/config');
    return response.data;
  },

  getWidgetHomepageContent: async (): Promise<WidgetHomepageContentResponse> => {
    const response: AxiosResponse<WidgetHomepageContentResponse> = await apiClient.get('/tenant/widget-homepage/content');
    return response.data;
  },

  getHomepageContentWithFAQs: async (apiKey: string): Promise<any> => {
    const response: AxiosResponse<any> = await axios.get(`${TENANT_API_BASE_URL}/hp_content`, {
      headers: {
        'Authorization': `Bearer ${apiKey}`
      }
    });
    return response.data;
  },

  previewWidgetHomepageContent: async (data: WidgetHomepageConfigRequest): Promise<{ preview_content: string; config: WidgetHomepageConfigRequest }> => {
    const response: AxiosResponse<{ preview_content: string; config: WidgetHomepageConfigRequest }> = await apiClient.post('/tenant/widget-homepage/preview', data);
    return response.data;
  },

  // Multiple Homepage Management
  getHomepages: async (): Promise<WidgetHomepageConfig[]> => {
    const response: AxiosResponse<WidgetHomepageConfig[]> = await apiClient.get('/tenant/widget-homepages/');
    return response.data;
  },

  createHomepage: async (data: MultipleHomepageCreateRequest): Promise<WidgetHomepageConfig> => {
    const response: AxiosResponse<WidgetHomepageConfig> = await apiClient.post('/tenant/widget-homepages/', data);
    return response.data;
  },

  getHomepage: async (id: string): Promise<WidgetHomepageConfig> => {
    const response: AxiosResponse<WidgetHomepageConfig> = await apiClient.get(`/tenant/widget-homepages/${id}`);
    return response.data;
  },

  updateHomepage: async (id: string, data: MultipleHomepageUpdateRequest): Promise<WidgetHomepageConfig> => {
    const response: AxiosResponse<WidgetHomepageConfig> = await apiClient.put(`/tenant/widget-homepages/${id}`, data);
    return response.data;
  },

  deleteHomepage: async (id: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.delete(`/tenant/widget-homepages/${id}`);
    return response.data;
  },

  duplicateHomepage: async (id: string): Promise<WidgetHomepageConfig> => {
    const response: AxiosResponse<WidgetHomepageConfig> = await apiClient.post(`/tenant/widget-homepages/${id}/duplicate`);
    return response.data;
  },

  previewHomepage: async (data: MultipleHomepageCreateRequest): Promise<{ preview_content: string; config: MultipleHomepageCreateRequest }> => {
    const response: AxiosResponse<{ preview_content: string; config: MultipleHomepageCreateRequest }> = await apiClient.post('/tenant/widget-homepage/preview', data);
    return response.data;
  },


  // Reviews
  fetchRandomReviews: async (): Promise<FetchReviewsResponse> => {
    const response: AxiosResponse<FetchReviewsResponse> = await apiClient.post('/fetch-reviews');
    return response.data;
  },

  // CTA Configuration
  getCTAConfig: async (): Promise<CTAConfigResponse> => {
    const response: AxiosResponse<CTAConfigResponse> = await apiClient.get('/ctas/config');
    return response.data;
  },

  updateCTAConfig: async (data: CTAConfigUpdateRequest): Promise<CTAConfigResponse> => {
    const response: AxiosResponse<CTAConfigResponse> = await apiClient.put('/ctas/config', data);
    return response.data;
  },

  // CTA Metrics
  getCTAMetrics: async (params?: CTAMetricsParams): Promise<CTAMetrics> => {
    const queryParams = new URLSearchParams();
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.traffic_type) queryParams.append('traffic_type', params.traffic_type);

    const url = `/tenant/dashboard/metrics/cta-clicks${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response: AxiosResponse<CTAMetrics> = await apiClient.get(url);
    return response.data;
  },

  // Message Text Metrics
  getMessageTextMetrics: async (params?: MessageTextMetricsParams): Promise<MessageTextMetrics> => {
    const queryParams = new URLSearchParams();
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.traffic_type) queryParams.append('traffic_type', params.traffic_type);
    if (params?.message_source) queryParams.append('message_source', params.message_source);

    const url = `/tenant/dashboard/metrics/message-text${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response: AxiosResponse<MessageTextMetrics> = await apiClient.get(url);
    return response.data;
  },

  // LLMS / AEO
  generateLLMSFile: async (payload?: { included_metadata: string[] }): Promise<LLMSGenerateResponse> => {
    const response: AxiosResponse<LLMSGenerateResponse> = await apiClient.post('/tenant/aeo/generate', payload ?? {});
    return response.data;
  },

  getFullSDL: async (includedMetadata: SDLMetadataOption[]): Promise<FullSDLResponse> => {
    const params = new URLSearchParams();
    params.set('included_metadata', includedMetadata.join(','));
    const response: AxiosResponse<FullSDLResponse> = await apiClient.get(
      `/questions/sdl/full?${params.toString()}`,
    );
    return response.data;
  },

  getSDLComparison: async (): Promise<SDLComparisonResponse> => {
    const response: AxiosResponse<SDLComparisonResponse> = await apiClient.get('/tenant/aeo/sdl-comparison');
    return response.data;
  },

  getLLMSGenerationStatus: async (jobId: string): Promise<LLMSGenerationStatusResponse> => {
    const response: AxiosResponse<LLMSGenerationStatusResponse> = await apiClient.get(`/tenant/aeo/generation-status/${jobId}`);
    return response.data;
  },

  downloadLLMSFile: async (): Promise<Blob> => {
    const response = await apiClient.get('/tenant/aeo/download', {
      responseType: 'blob'
    });
    return response.data;
  },

  getLLMSStatus: async (): Promise<LLMSCacheStatus | null> => {
    try {
      const response: AxiosResponse<LLMSCacheStatus | null> = await apiClient.get('/tenant/aeo/status');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  clearLLMSCache: async (): Promise<LLMSClearCacheResponse> => {
    const response: AxiosResponse<LLMSClearCacheResponse> = await apiClient.post('/tenant/aeo/clear-cache');
    return response.data;
  },

  // Tenant CRM Leads
  getLeads: async (search?: string, page: number = 1, limit: number = 10): Promise<PaginatedResponse<TenantCRMLead>> => {
    const params: any = { search: search || undefined, page, limit };
    // Remove undefined keys
    Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

    const response = await apiClient.get<PaginatedResponse<TenantCRMLead>>('/leads', { params });
    return response.data;
  },

  updateLead: async (leadId: string, data: TenantCRMLeadUpdate): Promise<TenantCRMLead> => {
    const response = await apiClient.put<TenantCRMLead>(`/leads/${leadId}`, data);
    return response.data;
  },

  deleteLead: async (leadId: string): Promise<void> => {
    await apiClient.delete(`/leads/${leadId}`);
  },

  syncLead: async (leadId: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(`/leads/${leadId}/sync`);
    return response.data;
  },

  // Question Labels
  getQuestionLabels: async (): Promise<QuestionLabel[]> => {
    const response: AxiosResponse<QuestionLabel[]> = await apiClient.get('/question-labels');
    return response.data;
  },

  createQuestionLabel: async (name: string): Promise<QuestionLabel> => {
    const response: AxiosResponse<QuestionLabel> = await apiClient.post('/question-labels', { name });
    return response.data;
  },

  updateQuestionLabel: async (id: string, name: string): Promise<QuestionLabel> => {
    const response: AxiosResponse<QuestionLabel> = await apiClient.put(`/question-labels/${id}`, { name });
    return response.data;
  },

  deleteQuestionLabel: async (id: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.delete(`/question-labels/${id}`);
    return response.data;
  },

  assignLabelsToQuestion: async (questionId: string, labelIds: string[]): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.post(`/questions/${questionId}/labels`, { label_ids: labelIds });
    return response.data;
  },

  removeLabelFromQuestion: async (questionId: string, labelId: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.delete(`/questions/${questionId}/labels/${labelId}`);
    return response.data;
  },

  clearAllCache: async (): Promise<{ success: boolean; message: string; tenant_id: string; tenant_subdomain: string; total_keys_cleared: number }> => {
    const response: AxiosResponse<{ success: boolean; message: string; tenant_id: string; tenant_subdomain: string; total_keys_cleared: number }> = await apiClient.post('/tenant/cache/clear-all');
    return response.data;
  },

  // Form Submissions
  getFormSubmissionStats: async (): Promise<FormSubmissionStats> => {
    const response: AxiosResponse<FormSubmissionStats> = await apiClient.get('/admin/form-submissions/stats');
    return response.data;
  },

  getTenantForms: async (
    formType?: FormType,
    status?: SubmissionStatus,
    limit?: number,
    offset?: number,
    query?: string
  ): Promise<PaginatedFormSubmissions> => {
    const params = new URLSearchParams();
    if (formType) params.append('form_type', formType);
    if (status) params.append('status', status);
    if (query) params.append('query', query);
    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());

    const url = `/admin/form-submissions${params.toString() ? '?' + params.toString() : ''}`;
    const response: AxiosResponse<PaginatedFormSubmissions> = await apiClient.get(url);
    return response.data;
  },

  // Conversion Events
  getConversionEvents: async (limit: number = 50, offset: number = 0): Promise<ConversionEventsResponse> => {
    const response: AxiosResponse<ConversionEventsResponse> = await apiClient.get(`/conversion-events?limit=${limit}&offset=${offset}`);
    return response.data;
  },

  // AI Authority
  getAIAuthority: async (): Promise<AIAuthorityReportList> => {
    const response: AxiosResponse<AIAuthorityReportList> = await apiClient.get('/tenant/ai-authority');
    return response.data;
  },

  /**
   * Full deep-dive report. Pass a reportId to select a specific synced report;
   * omit it to get the latest full report. 404 if not found for the tenant.
   */
  getAIAuthorityFull: async (reportId?: string): Promise<AIAuthorityDataFull> => {
    const response: AxiosResponse<AIAuthorityDataFull> = await apiClient.get('/tenant/ai-authority/full', {
      params: reportId ? { report_id: reportId } : undefined,
    });
    return response.data;
  },

  /**
   * Download the LARS PDF report for a given run (latest version).
   * Proxied through the ai-api — the LABS_FUNCTION_KEY stays server-side.
   */
  downloadLarsReport: async (runName: string): Promise<Blob> => {
    const response = await apiClient.get(
      `/tenant/lars/runs/${encodeURIComponent(runName)}/report/pdf`,
      { responseType: 'blob' },
    );
    return response.data;
  },

  /**
   * Download a versioned (archived) LARS PDF report by its report ID.
   * Falls back gracefully if the archive doesn't exist (pre-versioning reports).
   */
  downloadLarsReportById: async (runName: string, larsReportId: string): Promise<Blob> => {
    const response = await apiClient.get(
      `/tenant/lars/runs/${encodeURIComponent(runName)}/report/pdf/${encodeURIComponent(larsReportId)}`,
      { responseType: 'blob' },
    );
    return response.data;
  },


  // AI Assistant
  queryAIAssistant: async (params: AIAssistantQueryParams): Promise<AIAssistantQueryResponse> => {
    const response: AxiosResponse<AIAssistantQueryResponse> = await apiClient.post('/ai-assistant/query', params);
    return response.data;
  },

  // AI Question Management — propose a reviewable batch of question CRUD ops from a
  // natural-language instruction. Read-only on the server; the UI applies approved ops.
  planQuestionOps: async (params: QuestionOpsPlanParams): Promise<QuestionOpsPlanResponse> => {
    const response: AxiosResponse<QuestionOpsPlanResponse> = await apiClient.post('/ai-assistant/questions/plan', params);
    return response.data;
  },
};

// Set auth token
export const setAuthToken = (token: string) => {
  localStorage.setItem('tenant_auth_token', token);
};

// Get current auth token
export const getAuthToken = (): string | null => {
  return localStorage.getItem('tenant_auth_token');
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem('tenant_auth_token');
};
